//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { createContext } from 'react';

// -----------------------------------------------------------------------
// UI context value — app-level UI preferences.  Currently just the nav display
// mode (icon-titles, etc.).
// -----------------------------------------------------------------------

export interface UIContextValue {
  displayMode: string;
  // Set rather than dispatched, for the same reason setShowFrameRate is: the
  // value is kept on the profile, so there is no previous state here for a
  // functional update to fold into.
  setDisplayMode: (next: string) => void;
  // Whether a graph reports the rate it is being drawn at.  App-level rather than
  // per graph: it says something about this machine, not about any one picture.
  showFrameRate: boolean;
  // Set rather than dispatched: it is kept on the profile, so there is no previous value here
  // to fold a change into (see UIProvider).
  setShowFrameRate: (next: boolean) => void;
  // Whether a graph writes what it is asking of the renderer to the console.
  // Separate from the reading above: a rate says something is slow, this says
  // what, and wanting the one is not wanting a report every two seconds.
  //
  // Never true in a built copy: it is a switch for whoever is working on the renderer, and
  // what it costs -- a walk of the whole scene every two seconds -- is not something to leave
  // where a reader can find it (see OFFER_DIAGNOSTICS).
  logGraphDiagnostics: boolean;
  setLogGraphDiagnostics: React.Dispatch<React.SetStateAction<boolean>>;
}

const UIContext = createContext<UIContextValue | null>(null);

export { UIContext };
